import React, { useEffect, useState } from "react";
import ImageSlider, { photoList } from "@/components/ImageSlider";
import { goBack } from "@/components/BackButton";
import DirectionsShareButton from "@/components/maps/DirectionsShareButton";
import MapPreview from "@/components/maps/MapPreview";
import ShopAreaNotice from "@/components/maps/ShopAreaNotice";
import { openDirections as openMapDirections } from "@/components/maps/geo";
import { useParams, Link, useNavigate, useLocation } from "react-router-dom";
import { fetchPublicVendor, followVendor, unfollowVendor, fetchShopStatus, resolveImageUrl, trackShopContact } from "@/lib/api";
import { waUrl } from "@/lib/whatsapp";
import { contactGate } from "@/lib/contactGate";
import { useApp } from "@/context/AppContext";
import { ProductCard, ServiceCard } from "@/components/Cards";
import ShopStatusPill from "@/components/ShopStatusPill";
import ShareSheet from "@/components/ShareSheet";
import AuthDialog from "@/components/AuthDialog";
import ReviewsSection from "@/components/ReviewsSection";
import BusinessProfileSections from "@/components/profile/BusinessProfileSections";
import AddProductDialog from "@/components/vendor/AddProductDialog";
import { Edit3, Plus } from "lucide-react";
import HiddenItemsNote from "@/components/vendor/HiddenItemsNote";
import {
  MapPin, Star, Phone, Navigation, Share2, Heart, ShieldCheck,
  ArrowLeft, Store, MessageCircle, Users, Package, Wrench, BadgeCheck
} from "lucide-react";
import { toast } from "sonner";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function PublicVendorProfile() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const customerView = new URLSearchParams(useLocation().search).get("view") === "customer";
  const [ownerPreview, setOwnerPreview] = useState(false);
  const { pincode, user, isVendor } = useApp();
  const [vendor, setVendor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [authOpen, setAuthOpen] = useState(false);
  const [addProductOpen, setAddProductOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);

  const load = () => {
    setLoading(true);
    setNotFound(false);
    fetchPublicVendor(slug, pincode)
      .then((v) => {
        setOwnerPreview(customerView && !!v.is_owner);
        setVendor(customerView ? { ...v, is_owner: false } : v);
        // Update document head with dynamic OG for this vendor
        document.title = `${v.name} · VerB`;
        upsertMeta("description", v.tagline || v.description || `${v.name} — local shop on VerB`);
        upsertMeta("og:title", v.name, true);
        upsertMeta("og:description", (v.tagline || v.description || "").slice(0, 220), true);
        upsertMeta("og:image", v.image || "", true);
        upsertMeta("og:url", `${window.location.origin}/v/${v.slug || v.id}`, true);
        upsertMeta("og:type", "business.business", true);
      })
      .catch((e) => {
        if (e?.response?.status === 404) setNotFound(true);
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [slug, pincode]);


  // Real-time status polling every 30s
  useEffect(() => {
    if (!vendor?.id) return;
    const iv = setInterval(async () => {
      try {
        const res = await fetchShopStatus(vendor.id);
        setVendor((prev) => prev ? { ...prev, status: res.status } : prev);
      } catch { /* transient network error: keep last status, next poll retries */ }
    }, 30000);
    return () => clearInterval(iv);
  }, [vendor?.id]);

  const openDirections = () => {
    if (!vendor?.route_url) {
      const pin = vendor?.pincode;
      return toast.info(pin
        ? `Is shop ki exact location ya poora address saved nahi hai — sirf PIN ${pin}${vendor.location ? ` (${vendor.location})` : ""} pata hai. "PIN area map par dekhein" se area dekhein, ya Call/WhatsApp karke pata poochein.`
        : "Is shop ki location, address ya PIN saved nahi hai, isliye directions nahi khul sakte. Call ya WhatsApp karke pata poochein.");
    }
    openMapDirections(vendor.route_url);
  };

  const callShop = () => {
    if (contactGate(user, vendor?.contact_locked)) return;
    if (!vendor?.phone) return toast.info("Phone number not listed.");
    trackShopContact(vendor.id, "call");
    window.location.href = `tel:${String(vendor.phone).replace(/\s+/g, "")}`;
  };

  const whatsappShop = () => {
    if (contactGate(user, vendor?.contact_locked)) return;
    if (!vendor?.phone) return toast.info("Phone number not listed.");
    trackShopContact(vendor.id, "whatsapp");
    const digits = String(vendor.phone).replace(/\D/g, "");
    // Prepend 91 if 10-digit Indian number
    const full = digits.length === 10 ? `91${digits}` : digits;
    const msg = encodeURIComponent(`Hi ${vendor.name}, I found you on VerB.`);
    window.open(`https://wa.me/${full}?text=${msg}`, "_blank", "noopener");
  };

  const toggleFollow = async () => {
    if (!user) { setAuthOpen(true); return; }
    try {
      if (vendor.following) {
        const res = await unfollowVendor(vendor.slug || vendor.id);
        setVendor((v) => ({ ...v, following: false, followers_count: res.followers_count }));
        toast.success("Unfollowed");
      } else {
        const res = await followVendor(vendor.slug || vendor.id);
        setVendor((v) => ({ ...v, following: true, followers_count: res.followers_count }));
        toast.success(`You are now following ${vendor.name}`);
      }
    } catch {
      toast.error("Something went wrong");
    }
  };

  if (loading) return <div className="max-w-4xl mx-auto px-4 py-16 text-center text-slate-500" data-testid="vendor-profile-loading">Loading shop...</div>;
  if (notFound) return (
    <div className="max-w-4xl mx-auto px-4 py-16 text-center" data-testid="vendor-profile-404">
      <Store className="w-10 h-10 text-slate-300 mx-auto mb-3" />
      <div className="font-display text-2xl font-bold">Shop not found</div>
      <p className="text-sm text-slate-500 mt-1">This link is invalid or the vendor has removed their profile.</p>
      <Link to="/" className="inline-flex items-center gap-1 mt-4 text-emerald-700 font-semibold text-sm">Back to home →</Link>
    </div>
  );
  if (!vendor) return null;

  const shareUrl = `${window.location.origin}/v/${vendor.slug || vendor.id}`;
  const profileUrl = shareUrl;
  const isOwner = user && vendor.vendor_id && user.id === vendor.vendor_id; // Not exposed publicly, but fallback below
  const canManage = isVendor && user; // Show 'edit' if user is a vendor and viewing own — simple check

  return (
    <div data-testid="vendor-profile-page">
      {ownerPreview && (
        <div data-testid="customer-view-bar">
          <div className="bg-emerald-800 text-white text-xs sm:text-sm px-4 py-2 flex items-center gap-3">
            <span className="flex-1 min-w-0"><b>Customer View</b> · customers aapki shop aise dekhte hain (aapki privacy settings ke saath)</span>
            <button onClick={() => navigate("/vendor?toolbag=1")} className="flex-shrink-0 rounded-full bg-white text-emerald-800 font-semibold px-3 py-1 hover:bg-emerald-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-400" data-testid="customer-view-back-btn">Back to Tool Bag</button>
          </div>
          <HiddenItemsNote returnTo={`/v/${slug}?view=customer`} />
        </div>
      )}
      {vendor.listed === false && vendor.is_owner && (
        <div className="bg-amber-50 border-b border-amber-200 text-amber-900 text-xs sm:text-sm px-4 py-2 text-center" data-testid="shop-hidden-banner">
          Aapki shop abhi <b>Inactive (hidden)</b> hai — customers ise nahi dekh sakte. Vendor Dashboard → Shop Status se dobara Active karein.
        </div>
      )}
      {/* Cover */}
      <div className="relative h-44 sm:h-60 lg:h-72 overflow-hidden bg-slate-100">
        {vendor.image ? (
          <><ImageSlider images={photoList(vendor)} captions={vendor.image_captions} alt={vendor.name} overlayBottom="bottom-16 sm:bottom-20" badgeClass="top-12 right-3 sm:top-16" className="w-full h-full" testid="vendor-cover-slider" /></>
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-emerald-100 to-amber-50" />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent pointer-events-none" />
        <div className="absolute inset-x-0 top-0 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between text-white">
          <button onClick={() => goBack(navigate, "/category/shops")} className="inline-flex items-center gap-1.5 text-xs sm:text-sm bg-black/55 hover:bg-black/70 backdrop-blur-sm rounded-full px-3 py-1.5 font-semibold shadow" data-testid="vendor-back-btn">
            <ArrowLeft className="w-3.5 h-3.5" /> Back
          </button>
          <button onClick={() => setShareOpen(true)} className="inline-flex items-center gap-1.5 text-xs sm:text-sm bg-white/95 text-slate-900 hover:bg-white rounded-full px-3 py-1.5 font-semibold shadow" data-testid="vendor-share-btn">
            <Share2 className="w-3.5 h-3.5" /> Share
          </button>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Flashcard */}
        <div className="-mt-16 sm:-mt-20 relative z-20">
          <div className="bg-white rounded-3xl border border-slate-200/70 shadow-xl shadow-slate-900/5 p-5 sm:p-7" data-testid="vendor-flashcard">
            <div className="flex items-start gap-4 flex-wrap">
              {/* Logo */}
              <div className="-mt-12 sm:-mt-14 w-20 h-20 sm:w-24 sm:h-24 rounded-2xl overflow-hidden bg-emerald-50 ring-4 ring-white shadow-xl flex-shrink-0" data-testid="vendor-logo">
                {vendor.logo || vendor.image ? (
                  <img src={resolveImageUrl(vendor.logo || vendor.image)} alt="" className="w-full h-full object-cover" data-testid="vendor-logo-img" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-emerald-800 font-display font-bold text-xl">{(vendor.name || "S").slice(0, 1)}</div>
                )}
              </div>

              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <h1 className="font-display text-xl sm:text-2xl lg:text-3xl font-bold text-slate-900 leading-tight break-words min-w-0" data-testid="vendor-name">{vendor.name}</h1>
                  {vendor.reviews > 100 && <BadgeCheck className="w-5 h-5 text-emerald-600" title="Verified" />}
                  <ShopStatusPill status={vendor.status} shopId={vendor.id} size="lg" />
                </div>
                {vendor.owner && <div className="text-xs sm:text-sm text-slate-500 mt-1">by {vendor.owner_profile_id
                  ? <Link to={`/p/${vendor.owner_profile_id}`} className="font-bold text-[rgb(var(--vb-primary-dark))] hover:underline underline-offset-4" data-testid="vendor-owner-link">{vendor.owner}</Link>
                  : <span className="font-semibold text-slate-700" data-testid="vendor-owner-name">{vendor.owner}</span>}</div>}
                {vendor.categories?.length > 0 && (
                  <div className="flex gap-1.5 flex-wrap mt-1.5">
                    {vendor.categories.map((c) => (
                      <span key={c} className="text-[10px] uppercase tracking-wider bg-amber-50 text-amber-800 border border-amber-200 px-2.5 py-0.5 rounded-full font-semibold">{c}</span>
                    ))}
                  </div>
                )}
                {vendor.tagline && <p className="text-sm text-slate-600 mt-2 line-clamp-2">{vendor.tagline}</p>}

                <div className="flex items-center gap-3 mt-3 text-xs text-slate-600 flex-wrap">
                  <div className="flex items-center gap-1 bg-emerald-50 text-emerald-800 px-2 py-0.5 rounded font-semibold">
                    <Star className="w-3 h-3 fill-current" /> {vendor.rating || "-"}
                  </div>
                  {vendor.reviews ? <span>{vendor.reviews} reviews</span> : null}
                  {vendor.followers_count > 0 && (
                    <span className="inline-flex items-center gap-1"><Users className="w-3 h-3" /> {vendor.followers_count} followers</span>
                  )}
                  {vendor.distance_km !== null && vendor.distance_km !== undefined && (
                    <span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3" /> {vendor.distance_km} km</span>
                  )}
                </div>
              </div>
            </div>

            {/* Action row */}
            <div className={`grid ${vendor.phone || vendor.contact_locked ? "grid-cols-4 sm:grid-cols-5" : "grid-cols-3"} gap-2 mt-6`} data-testid="vendor-actions">
              {(vendor.phone || vendor.contact_locked) && <ActionBtn onClick={whatsappShop} icon={MessageCircle} label="WhatsApp" testid="vendor-whatsapp-btn" primary />}
              {(vendor.phone || vendor.contact_locked) && <ActionBtn onClick={callShop} icon={Phone} label="Call" testid="vendor-call-btn" />}
              <ActionBtn onClick={openDirections} icon={Navigation} label="Directions" testid="vendor-directions-btn" />
              <ActionBtn onClick={() => setShareOpen(true)} icon={Share2} label="Share" testid="vendor-share-action-btn" />
              <ActionBtn onClick={toggleFollow} icon={Heart} label={vendor.following ? "Following" : "Follow"} testid="vendor-follow-btn" active={vendor.following} />
            </div>
            {vendor.directions_url && <div className="mt-2 flex justify-center sm:justify-start"><DirectionsShareButton shopName={vendor.name} url={vendor.directions_url} testid="vendor-directions-share-btn" /></div>}

            {/* Address */}
            <div className="mt-5 pt-5 border-t border-slate-100 text-xs sm:text-sm text-slate-600 grid sm:grid-cols-2 gap-2 [&>div]:rounded-xl [&>div]:bg-[rgb(var(--vb-cream))] [&>div]:border [&>div]:border-slate-100 [&>div]:px-3 [&>div]:py-2.5 [&>div]:min-w-0 [&>div]:break-words" data-testid="vendor-info-rows">
              {vendor.address && <div className="inline-flex items-start gap-1.5"><MapPin className="w-3.5 h-3.5 text-emerald-700 mt-0.5 flex-shrink-0" /> <span>{vendor.address}{vendor.landmark ? ` · ${vendor.landmark}` : ""}</span></div>}
              {(vendor.pincode || vendor.location) && <div className="inline-flex items-center gap-1.5"><ShieldCheck className="w-3.5 h-3.5 text-emerald-700 flex-shrink-0" /> PIN {vendor.pincode} · {vendor.location || ""}</div>}
              <div className="sm:col-span-2 !p-0 !bg-transparent !border-0" data-testid="vendor-map-wrap">
                {vendor.map_embed_url ? <MapPreview embedUrl={vendor.map_embed_url} viewUrl={vendor.map_view_url} testid="vendor-map" /> : <ShopAreaNotice vendor={vendor} />}
              </div>
              {vendor.delivery_radius_enabled && vendor.delivery_radius_km && (
                <div className="inline-flex items-center gap-1.5" data-testid="vendor-delivery-area"><Navigation className="w-3.5 h-3.5 text-amber-700 flex-shrink-0" /> Delivers within {vendor.delivery_radius_km} km</div>
              )}
              <div className="inline-flex items-center gap-1.5" data-testid="public-delivery-today">
                <Navigation className={`w-3.5 h-3.5 flex-shrink-0 ${vendor.delivery_today_available !== false ? "text-emerald-700" : "text-rose-700"}`} />
                Delivery Available Today: <span className="font-semibold">{vendor.delivery_today_available !== false ? "Yes" : "No"}</span>
              </div>
            </div>

            {Array.isArray(vendor.delivery_slabs) && vendor.delivery_slabs.length > 0 && (
              <div className="mt-3 bg-emerald-50/60 border border-emerald-100 rounded-xl p-3" data-testid="public-delivery-slabs">
                <div className="text-[11px] uppercase tracking-wider font-semibold text-emerald-800 mb-1.5">Delivery charges</div>
                <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-700">
                  {vendor.delivery_slabs.map((s, i) => (
                    <span key={i} data-testid={`public-slab-${i}`}>Up to {s.km} km — <b>₹{s.price}</b></span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>


        {vendor.business_profile_enabled && <BusinessProfileSections name={vendor.name} profile={vendor.business_profile} />}

        {/* Products */}
        <section className="mt-10" data-testid="vendor-products-section">
          <div className="flex items-center justify-between gap-2 mb-4 flex-wrap">
            <h2 className="font-display text-xl sm:text-2xl font-bold flex items-center gap-2"><Package className="w-5 h-5 text-emerald-700" /> Products ({vendor.products?.length || 0})</h2>
            {vendor.is_owner && (
              <button
                onClick={() => { setEditingProduct(null); setAddProductOpen(true); }}
                className="inline-flex items-center gap-1.5 rounded-full bg-emerald-700 hover:bg-emerald-800 text-white px-4 py-2 text-sm font-semibold shadow-sm"
                data-testid="owner-add-product-btn"
              >
                <Plus className="w-4 h-4" /> Add product
              </button>
            )}
          </div>
          {vendor.products?.length ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 lg:gap-4">
              {vendor.products.map((p) => (
                <div key={p.id} className="relative">
                  <ProductCard product={p} />
                  {vendor.is_owner && (
                    <button
                      onClick={(e) => { e.preventDefault(); setEditingProduct(p); setAddProductOpen(true); }}
                      className="absolute top-2 right-2 z-20 bg-white/95 hover:bg-emerald-700 hover:text-white text-slate-800 rounded-full p-1.5 shadow-md transition-colors"
                      aria-label="Edit"
                      data-testid={`owner-edit-product-${p.id}`}
                    >
                      <Edit3 className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="text-sm text-slate-500 py-6 bg-white border border-slate-100 rounded-2xl text-center" data-testid="vendor-products-empty">
              {vendor.is_owner ? "No products yet — tap “Add product” above to list your first item." : "No products listed yet."}
            </div>
          )}
        </section>

        {/* Services */}
        {/* Services — always rendered for the owner so they can add one in one tap */}
        {(vendor.services?.length > 0 || vendor.is_owner) && (
          <section className="mt-10" data-testid="vendor-services-section">
            <div className="flex items-center justify-between gap-2 mb-4 flex-wrap">
              <h2 className="font-display text-xl sm:text-2xl font-bold flex items-center gap-2"><Wrench className="w-5 h-5 text-amber-700" /> Services ({vendor.services?.length || 0})</h2>
              {vendor.is_owner && (
                <button
                  onClick={() => navigate("/vendor?tab=services")}
                  className="inline-flex items-center gap-1.5 rounded-full bg-amber-600 hover:bg-amber-700 text-white px-4 py-2 text-sm font-semibold shadow-sm"
                  data-testid="owner-add-service-btn"
                >
                  <Plus className="w-4 h-4" /> Add service
                </button>
              )}
            </div>
            {vendor.services?.length ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 lg:gap-4">
                {vendor.services.map((s) => <ServiceCard key={s.id} service={s} />)}
              </div>
            ) : (
              <div className="text-sm text-slate-500 italic bg-white border border-dashed border-slate-200 rounded-2xl p-6 text-center" data-testid="no-services-empty">
                No services listed yet. Tap "Add service" to list your first offering.
              </div>
            )}
          </section>
        )}

        <ReviewsSection
          slug={vendor.slug || vendor.id}
          isOwner={!!vendor.is_owner}
          shopName={vendor.name}
          onRatingChanged={(r, c) => setVendor((v) => v ? { ...v, rating: r, reviews: c } : v)}
        />

        <div className="h-14" />
      </div>

      <ShareSheet
        open={shareOpen}
        onClose={() => setShareOpen(false)}
        shopName={vendor.name}
        tagline={vendor.tagline || vendor.description}
        shareUrl={shareUrl}
        profileUrl={profileUrl}
        shop={vendor}
      />
      <AuthDialog open={authOpen} onOpenChange={setAuthOpen} />
      {vendor.is_owner && (
        <AddProductDialog
          open={addProductOpen}
          onOpenChange={(o) => { setAddProductOpen(o); if (!o) setEditingProduct(null); }}
          shops={[{ shop_id: vendor.id, name: vendor.name }]}
          product={editingProduct}
          onCreated={load}
          onSaved={load}
        />
      )}
    </div>
  );
}

function ActionBtn({ onClick, icon: Icon, label, testid, disabled, active, primary }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`flex items-center justify-center gap-1.5 rounded-2xl text-[11px] sm:text-xs font-semibold transition-colors border ${primary ? "col-span-4 sm:col-span-1 flex-row sm:flex-col py-3 sm:py-2.5 text-sm sm:text-xs bg-emerald-600 hover:bg-emerald-700 text-white border-emerald-600 shadow-md shadow-emerald-700/20" : "flex-col py-2.5"} ${primary ? "" : active ? "bg-emerald-700 text-white border-emerald-700" : disabled ? "bg-slate-50 text-slate-300 border-slate-100 cursor-not-allowed" : "bg-white text-slate-700 border-slate-200 hover:border-emerald-500 hover:text-emerald-800"}`}
      data-testid={testid}
    >
      <Icon className="w-4 h-4" /> {label}
    </button>
  );
}

function upsertMeta(name, content, isProperty = false) {
  const attr = isProperty ? "property" : "name";
  let el = document.head.querySelector(`meta[${attr}="${name}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content || "");
}
